<template>
  <section class="navbar-drawer-section">
    <v-app-bar id="main-menu-navigation"
               v-if="menu"
               v-bind="menu.properties">
      <template
          v-if="menu.logo"
      >
        <v-toolbar-title>
          <dynamic-logo :ready="ready" :mobileOnly="false" :context="menu.logo"/>
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </template>
      <template
          v-if="menu.items"
      >
        <!--  if not mobile    -->
        <template v-if="!mobile">
          <template v-for="(menuItem, index) in menu.items">
            <a
                v-if="menuItem.visible && (!menuItem.items || menuItem.items.length === 0)"
                :key="index"
                class="d-none d-lg-block d-md-block menu-0-full"
                :class="{'active': isActiveMenu(menuItem) }"
                v-bind="menuItem.properties"
                :href="getURLLocalGlobal(menuItem.route ? menuItem.route.url : '#')"
                :target="menuItem.route ? menuItem.route.target : null"
            >
              {{ menuItem.properties.label }}

              <v-icon
                  v-if="menuItem.icon"
                  v-bind="menuItem.icon.properties"
              >
                {{ menuItem.icon.value }}
              </v-icon>
            </a>

            <v-menu
                :key="index"
                v-else-if="menuItem.visible"
                class="d-none d-lg-block d-md-block"
                open-on-hover
                offset-y
                :close-on-content-click="false"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-list-group
                    v-bind="attrs"
                    v-on="on"
                >

                  <template v-slot:activator>
                    <a
                        :class="{'active': isActiveMenu(menuItem) }"
                        class="d-none d-lg-block d-md-block"
                        v-bind="menuItem.properties"
                        :href="getURLLocalGlobal(menuItem.route ? menuItem.route.url : '#')"
                        :target="menuItem.route ? menuItem.route.target : null"
                    >
                      {{ menuItem.properties.label }}
                    </a>
                  </template>

                  <template v-slot:appendIcon>
                    <v-icon class="d-none d-lg-block d-md-block" v-bind="menuItem.properties">
                      mdi-chevron-{{ attrs['aria-expanded'] === 'true' ? 'up' : 'down' }}
                    </v-icon>
                  </template>
                </v-list-group>
              </template>

              <dynamic-menu
                  :mobile="false"
                  :level="level"
                  :ready="ready"
                  :context="menuItem"
                  class="d-none d-lg-block d-md-block"
                  :navigate="navigate"
              />

            </v-menu>
          </template>

        </template>

        <!--  if mobile    -->
        <template v-else>
          <v-app-bar-nav-icon @click.stop="drawer = !drawer"/>
        </template>
      </template>
      <slot name="search-box"></slot>
    </v-app-bar>
    <v-navigation-drawer
        v-model="drawer"
        absolute
        left
        temporary
        color="transparent"
    >
      <a :style="{'padding': '27px 15px 20px !important'}"
         @click.stop="drawer = !drawer">
        <v-icon>mdi-menu</v-icon>
        <dynamic-logo class="ml-3" :ready="ready" :mobileOnly="true" :context="menu.logo"/>
      </a>
      <v-divider/>
      <dynamic-menu
          :mobile="true"
          :level="level"
          :ready="ready"
          :context="menu"
          :navigate="navigate"
      />
      <slot name="mobile-menu"></slot>
    </v-navigation-drawer>
  </section>
</template>

<script>

import globalMixins from "@/mixins/globalMixins";
import DynamicMenu from "@/components/DynamicMenu";
import DynamicLogo from "@/components/DynamicLogo";

export default {
  name: 'MainMenu',

  mixins: [globalMixins],

  components: {
    DynamicMenu,
    DynamicLogo
  },

  data() {
    return {
      level: 0,
      ready: false,
      mobile: false,
      drawer: false,
      defaultMenuClose: false,
      menu: {
        icon: {},
        properties: {},
        items: []
      }
    }
  },
  props: {
    context: {
      type: [Object, Array]
    }
  },

  async created() {
    this.checkIfMobile(this.$screen);
    await this.init();
  },

  methods: {
    checkIfMobile(to) {
      //if not lg and not xl and not 2xl return true
      this.mobile = !to.lg && !to.xl && !to['2xl'];
    },

    async init() {
      const _self = this;
      this.ready = false;

      let loggedInUser = this.$store.getters['auth/getUser'] || {};

      let menu = this._.cloneDeep(this.context);
      if (Array.isArray(menu)) {
        menu = menu[0];
      }

      await this.normalizeMenuFromAPIResponse(menu);

      async function fixEntries(menu) {
        let list = menu.items;

        return await Promise.all(list.map(async (oneItem) => {
          oneItem.route = _self._.cloneDeep(oneItem.properties.route);
          delete oneItem.properties.route;

          if (oneItem.route) {
            oneItem.properties.label = oneItem.route.name;
            oneItem.properties.id = oneItem.route.name;

            if (oneItem.route.access) {
              oneItem.access = _self._.cloneDeep(oneItem.route.access);
              delete oneItem.route.access;
            }
          }

          if (oneItem.properties.icon) {
            oneItem.icon = _self._.cloneDeep(oneItem.properties.icon);
            delete oneItem.properties.icon;
          }

          if (oneItem.properties.class)
            oneItem.properties.class = oneItem.properties.class.toLowerCase();

          if (oneItem.properties.color)
            oneItem.properties.color = oneItem.properties.color.toLowerCase();

          if (oneItem.properties.items) {
            oneItem.items = _self._.cloneDeep(oneItem.properties.items);
            delete oneItem.properties.items;
            _self.$forceUpdate();
          }

          //set new access option based on app type.
          if (process.env.VUE_APP_TYPE === "widget" && oneItem.access) {
            let access = false;
            if (loggedInUser && loggedInUser.groups) {
              loggedInUser.groups.forEach((oneUserGroup) => {
                if (oneItem.access.includes(oneUserGroup.label)) {
                  access = true;
                }
              });
            }
            oneItem.visible = access;
          } else {
            oneItem.visible = true;
          }

          if (oneItem.items && Array.isArray(oneItem.items)) {
            await fixEntries(oneItem);
            await _self.normalizeMenuFromAPIResponse(oneItem);
          }
        }));

      }

      if (menu && menu.items && Array.isArray(menu.items) && menu.items.length > 0) {
        await fixEntries(menu);
      }

      await this.checkFixProperties(menu);

      for (let i in menu.properties) {
        if (i.includes('v-')) {
          let newI = i.replace('v-', '');
          menu.properties[newI] = menu.properties[i];
          delete menu.properties[i];
        }
      }

      this.menu = menu;
      this.$forceUpdate();
      this.ready = true;

      return true;
    },

    async normalizeMenuFromAPIResponse(menu) {
      if (!menu.items && menu.properties && menu.properties.items) {
        menu.items = this._.cloneDeep(menu.properties.items);
        delete menu.properties.items;
      }

      if (menu.properties.logo && Array.isArray(menu.properties.logo)) {
        let logo = menu.properties.logo[0];
        if (logo.route || logo.properties) {
          menu.logo = logo;
        }
        delete menu.properties.logo;
      }
      if (menu.logo) {
        menu.logo.route = this._.cloneDeep(menu.logo.properties.url);
        delete menu.logo.properties.url;
      }

      if (menu.properties.icon && Array.isArray(menu.properties.icon)) {
        menu.icon = menu.properties.icon[0];
        delete menu.properties.icon;
      }

      for (let i in menu.properties) {
        if (typeof menu.properties[i] === 'string') {
          menu.properties[i] = menu.properties[i].toLowerCase();
        }
      }

      this.$forceUpdate();
      return true;
    },

    navigate(menu, $event) {
      $event.preventDefault();
      this.defaultMenuClose = false;
      if (menu && menu.route && menu.route.url !== '#') {
        this.goToPage(menu, $event, () => {
          this.defaultMenuClose = true;
        });
      }
    }
  },

  watch: {
    async '$screen'(to) {
      this.checkIfMobile(to);
    }
  }
};
</script>
<style>
.v-btn--plain:not(.v-btn--active):not(.v-btn--loading):not(:focus):not(:hover) .v-btn__content {
  opacity: 1;
}

section.navbar-drawer-section {
  position: initial;
  display: contents;
}

</style>
